// Chemin: C:\gestion-copro\src\context\AuthContext.tsx
// Context d'authentification pour ORION PILOT
// Session 106 : CORRECTION BUG CRITIQUE — suppression création automatique profil gestionnaire
// Session 200 : browserLocalPersistence + timeout 5s
// Session 201 : timeout 3s + fix loading bloqué après signIn
// Session 241 : Appel enregistrerPremierLoginDemo au login si profile.estDemo === true
//               Fire-and-forget — une erreur ne bloque pas l'accès
// Session 243 : Chrono démo — stockage dateExpiration dans le context
//               Intervalle 30s → modal bloquante puis déconnexion automatique à expiration

import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User as FirebaseUser } from 'firebase/auth';
import { setPersistence, browserLocalPersistence } from 'firebase/auth';
import { auth } from '../services/firebase';
import { UserProfile } from '../types/user.types';
import { 
  onAuthChange, 
  getUserProfile, 
  signIn as authSignIn, 
  signOut as authSignOut,
  isGestionnaire,
  isConseilSyndical
} from '../services/auth.service';
import { enregistrerPremierLoginDemo } from '../services/demo.service';

interface AuthContextType {
  firebaseUser: FirebaseUser | null;
  userProfile: UserProfile | null;
  loading: boolean;
  error: string | null;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  clearError: () => void;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isCS: boolean;
  // Session 243 : Chrono démo
  demoExpiration: Date | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Session 243 : Chrono démo
  const [demoExpiration, setDemoExpiration] = useState<Date | null>(null);
  const [showDemoExpiredModal, setShowDemoExpiredModal] = useState(false);

  useEffect(() => {
    setPersistence(auth, browserLocalPersistence).catch((err) => {
      console.warn('setPersistence error:', err);
    });

    // Timeout de sécurité 3s
    const timeoutId = setTimeout(() => {
      setLoading((prev) => {
        if (prev) {
          console.warn('AuthContext : timeout Firebase Auth (3s) — déblocage forcé');
          return false;
        }
        return prev;
      });
    }, 3000);

    const unsubscribe = onAuthChange(async (user) => {
      clearTimeout(timeoutId);
      setFirebaseUser(user);
      
      if (user) {
        try {
          const profile = await getUserProfile(user.uid);
          
          if (!profile) {
            console.warn('Aucun profil Firestore pour', user.email);
            setUserProfile(null);
            setError('Compte non configuré. Contactez l\'administrateur ou inscrivez-vous.');
          } else {
            if (profile.prenom) {
              profile.prenom = profile.prenom.charAt(0).toUpperCase() + profile.prenom.slice(1).toLowerCase();
            }
            if (profile.nom) {
              profile.nom = profile.nom.toUpperCase();
            }
            setUserProfile(profile);

            // Session 241/243 : Si compte démo, enregistrer le premier login (idempotent)
            // Fire-and-forget — une erreur ne bloque pas l'accès
            if (profile.estDemo) {
              enregistrerPremierLoginDemo().then((res) => {
                if (res.premierLogin) {
                  console.log('[AuthContext] Premier login démo enregistré — expiration:', res.dateExpiration);
                }
                // Session 243 : stocker dateExpiration même si ce n'est pas le premier login
                if (res.dateExpiration) {
                  setDemoExpiration(new Date(res.dateExpiration));
                }
              }).catch((err) => {
                console.warn('[AuthContext] enregistrerPremierLoginDemo échoué (non bloquant):', err);
              });
            }
          }
        } catch (err) {
          console.error('Erreur chargement profil:', err);
          setError('Erreur lors du chargement du profil');
        }
      } else {
        setUserProfile(null);
      }
      
      setLoading(false);
    });

    return () => {
      clearTimeout(timeoutId);
      unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string): Promise<void> => {
    setError(null);
    try {
      // Fix session 242 : si une session active existe avec un autre compte, la fermer d'abord
      // Evite que Firebase recharge l'ancienne session lors d'une connexion demo en navigation normale
      if (auth.currentUser && auth.currentUser.email !== email) {
        await authSignOut();
      }
      await authSignIn(email, password);
    } catch (err: any) {
      console.error('Erreur connexion:', err);
      
      let message = 'Erreur de connexion';
      switch (err.code) {
        case 'auth/user-not-found':
          message = 'Aucun compte trouvé avec cet email';
          break;
        case 'auth/wrong-password':
          message = 'Mot de passe incorrect';
          break;
        case 'auth/invalid-email':
          message = 'Adresse email invalide';
          break;
        case 'auth/too-many-requests':
          message = 'Trop de tentatives. Réessayez plus tard';
          break;
        case 'auth/invalid-credential':
          message = 'Email ou mot de passe incorrect';
          break;
        default:
          message = err.message || 'Erreur de connexion';
      }
      
      setError(message);
      throw new Error(message);
    }
  };

  const signOut = async (): Promise<void> => {
    setError(null);
    try {
      await authSignOut();
      setUserProfile(null);
      setFirebaseUser(null);
      setDemoExpiration(null); // Session 243 : reset chrono démo
    } catch (err: any) {
      console.error('Erreur déconnexion:', err);
      setError('Erreur lors de la déconnexion');
    }
  };

  const clearError = () => setError(null);

  // Session 243 : Intervalle 30s — déconnexion automatique à expiration démo
  useEffect(() => {
    if (!demoExpiration) return;

    const intervalle = setInterval(() => {
      const maintenant = new Date();
      if (maintenant >= demoExpiration) {
        clearInterval(intervalle);
        setShowDemoExpiredModal(true);
      }
    }, 30000); // vérification toutes les 30 secondes

    // Vérification immédiate au montage (cas où on charge une session déjà expirée)
    if (new Date() >= demoExpiration) {
      setShowDemoExpiredModal(true);
    }

    return () => clearInterval(intervalle);
  }, [demoExpiration]);

  // Handler déconnexion après modal expiration
  const handleDemoExpiredSignOut = async () => {
    setShowDemoExpiredModal(false);
    setDemoExpiration(null);
    await signOut();
  };

  const value: AuthContextType = {
    firebaseUser,
    userProfile,
    loading,
    error,
    signIn,
    signOut,
    clearError,
    isAuthenticated: !!firebaseUser,
    isAdmin: isGestionnaire(userProfile),
    isCS: isConseilSyndical(userProfile),
    demoExpiration, // Session 243
  };

  return (
    <AuthContext.Provider value={value}>
      {loading ? (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          background: '#f8fafc',
          flexDirection: 'column',
          gap: '16px',
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '3px solid #e2e8f0',
            borderTop: '3px solid #1e40af',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite',
          }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          <span style={{ color: '#94a3b8', fontSize: '13px', fontFamily: 'sans-serif' }}>
            Chargement…
          </span>
        </div>
      ) : (
        <>
          {children}
          {/* Session 243 : Modal expiration démo */}
          {showDemoExpiredModal && (
            <div style={{
              position: 'fixed', inset: 0, zIndex: 9999,
              background: 'rgba(15, 23, 42, 0.75)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: '24px',
            }}>
              <div style={{
                background: '#ffffff',
                borderRadius: '16px',
                padding: '40px 36px',
                maxWidth: '440px',
                width: '100%',
                textAlign: 'center',
                boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
              }}>
                <div style={{
                  width: '64px', height: '64px',
                  background: '#fef3c7',
                  borderRadius: '50%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  margin: '0 auto 24px',
                  fontSize: '28px',
                }}>
                  ⏱
                </div>
                <h2 style={{
                  margin: '0 0 12px',
                  fontSize: '20px',
                  fontWeight: '700',
                  color: '#0f172a',
                  fontFamily: 'sans-serif',
                }}>
                  Votre session de démonstration est terminée
                </h2>
                <p style={{
                  margin: '0 0 32px',
                  fontSize: '15px',
                  color: '#64748b',
                  lineHeight: '1.6',
                  fontFamily: 'sans-serif',
                }}>
                  Merci d'avoir découvert Copro Protect. Pour en savoir plus ou souscrire un abonnement, contactez-nous.
                </p>
                <button
                  onClick={handleDemoExpiredSignOut}
                  style={{
                    background: '#1e40af',
                    color: '#ffffff',
                    border: 'none',
                    borderRadius: '10px',
                    padding: '14px 32px',
                    fontSize: '15px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    fontFamily: 'sans-serif',
                    width: '100%',
                  }}
                >
                  Fermer
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth doit être utilisé dans un AuthProvider');
  }
  return context;
};

export default AuthContext;
